import {TimeValue, useTimeField, useDateSegment, useLocale} from 'react-aria';
import {useTimeFieldState} from 'react-stately';
import type {DateSegment as DateSegmentType} from 'react-stately';
import {useRef} from "react";
import {ClockIcon} from "@heroicons/react/24/outline";

interface TimeRangeFieldProps {
    label?: string;
    isRequired?: boolean;
    isDisabled?: boolean;
    startValue?: TimeValue | null;
    endValue?: TimeValue | null;
    onChange?: (start: TimeValue | null, end: TimeValue | null) => void;
    description?: string;
    showIcon?: boolean;
    hourCycle?: 12 | 24;
}

export function TimeRangeField(props: TimeRangeFieldProps) {
    const {
        label,
        isRequired = false,
        isDisabled = false,
        startValue,
        endValue,
        onChange,
        description,
        showIcon = true,
        hourCycle = 24,
    } = props;

    const {locale} = useLocale();

    const startState = useTimeFieldState({
        value: startValue,
        onChange: (value) => onChange?.(value, endValue || null),
        locale,
        hourCycle,
    });

    const endState = useTimeFieldState({
        value: endValue,
        onChange: (value) => onChange?.(startValue || null, value),
        locale,
        hourCycle,
    });

    const startRef = useRef<HTMLDivElement | null>(null);
    const endRef = useRef<HTMLDivElement | null>(null);

    const {labelProps: startLabelProps, fieldProps: startFieldProps} = useTimeField(
        {
            'aria-label': 'Start time',
            hourCycle,
            isDisabled
        },
        startState,
        startRef
    );

    const {labelProps: endLabelProps, fieldProps: endFieldProps} = useTimeField(
        {
            'aria-label': 'End time',
            hourCycle,
            isDisabled,
        },
        endState,
        endRef
    );

    const isInvalid = startState.isInvalid || endState.isInvalid;

    return (
        <div>
            {label && (
                <label className={`field-label ${isDisabled ? 'field-disabled' : ''}`}>
                    {label}
                    {isRequired && <span className={'field-required'}>*</span>}
                </label>
            )}

            <div className={`field-container ${isDisabled ? 'field-disabled' : ''}`}>
                {showIcon && (
                    <ClockIcon className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                )}

                {/* Start Time */}
                <div {...startFieldProps} ref={startRef} className={'flex items-center'}>
                    {startState.segments.map((segment, i) => (
                        <TimeSegment key={i} segment={segment} state={startState}/>
                    ))}
                </div>

                <span className="text-gray-500 mx-2 flex-shrink-0">–</span>

                {/* End Time */}
                <div {...endFieldProps} ref={endRef} className={'flex items-center'}>
                    {endState.segments.map((segment, i) => (
                        <TimeSegment key={i} segment={segment} state={endState}/>
                    ))}
                </div>
            </div>

            {description && (
                <div className={'mt-2 text-xs text-gray-600'}>
                    {description}
                </div>
            )}

            {isInvalid && (
                <div className={'field-error'}>
                    Invalid time range
                </div>
            )}
        </div>
    );
}

function TimeSegment({segment, state}: {segment: DateSegmentType; state: any}) {
    const ref = useRef<HTMLDivElement | null>(null);
    const {segmentProps} = useDateSegment(segment, state, ref);

    return (
        <div
            {...segmentProps}
            ref={ref}
            className={`
                segment
                px-0.5
                text-right
                outline-none
                rounded-sm
                focus:bg-gray-100
                ${segment.isPlaceholder ? 'text-gray-400' : 'text-gray-900'}
                ${segment.type === 'literal' ? 'px-0' : ''}
            `}
            style={{
                ...segmentProps.style,
                minWidth: segment.type === 'literal' ? 'auto' : segment.maxValue != null && segment.maxValue > 99 ? '2.5em' : '1em',
            }}
        >
            {segment.text}
        </div>
    );
}